// @file Composable for inserting mentions into post body/comment
import { trackEvent } from '@@/bits/analytics'
import type { MentionType } from '@@/custom_elements/pdlt_mention'
import { PdltMention } from '@@/custom_elements/pdlt_mention'
import { useMentionsStore } from '@@/pinia/mentions'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import type { UserMentionSuggestion, UserWithHashId } from '@@/types'
import type { MentionEvent } from '@@/vuedirectives/mention_autocomplete'
import type { Editor } from '@tiptap/vue-2'
import { storeToRefs } from 'pinia'
import Trix from 'trix'
import type { Ref } from 'vue'

export const useMentionComposer = ({
  postBodyEditor,
  commentBodyEditor,
  editorLock,
}: {
  postBodyEditor?: { editor: Trix }
  commentBodyEditor?: { editor: Editor }
  editorLock?: Ref<boolean>
}): {
  insertMentionToPostBody: typeof insertMentionToPostBody
  insertMentionToCommentBody: typeof insertMentionToCommentBody
  loadHtmlToPostBody: typeof loadHtmlToPostBody
} => {
  const mentionsStore = useMentionsStore()

  const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())
  const { currentHighlightedSuggestion } = storeToRefs(mentionsStore)

  const { hideMentionBox } = mentionsStore

  /*
   * INSERTING MENTION TAGS
   */

  const insertMentionToPostBody = ({
    mentionRange,
    selectedSuggestion,
    mentioner,
  }: Pick<MentionEvent, 'mentionRange'> & {
    selectedSuggestion?: UserMentionSuggestion
    mentioner?: Pick<UserWithHashId, 'hashid'>
  }): UserMentionSuggestion | null => {
    const editor = postBodyEditor?.editor
    if (editor == null) return null

    const suggestion = selectedSuggestion ?? currentHighlightedSuggestion.value
    if (suggestion == null) return null

    mentioner = mentioner ?? currentUser.value
    if (mentioner == null) return null

    const mentionTag = PdltMention.fromAttributes({
      type: suggestion.type as MentionType,
      mentioner,
      mentionee: suggestion.attributes,
    }).toString()
    // Trix wraps the attachment in a <figure> tag. When sending the post body to the backend API, it contains
    // the <figure> tag as well but we strip it out (see `sanitize_helper.rb#EXCLUDED_ELEMENTS`). Because of
    // that, we decide not to do anything on the frontend and only rely on the backend to strip the unwanted
    // tag. But this is implicit so we need to take note here.
    const mention = new Trix.Attachment({ content: mentionTag, contentType: 'mention' })

    // Before inserting the mention tag, delete the mention text
    const rangeToDelete = mentionRange ?? editor.getSelectedRange()
    editor.setSelectedRange(rangeToDelete)
    editor.deleteInDirection('backward')

    // Add the mention tag as a Trix attachment
    // It can be moved or removed, but not edited directly
    editor.insertAttachment(mention)
    trackEvent('Mention', 'Added mention to post body')

    hideMentionBox()

    return suggestion
  }

  const insertMentionToCommentBody = ({
    mentionRange,
    selectedSuggestion,
    mentioner,
  }: Pick<MentionEvent, 'mentionRange'> & {
    selectedSuggestion?: UserMentionSuggestion
    mentioner?: Pick<UserWithHashId, 'hashid'>
  }): UserMentionSuggestion | null => {
    const editor = commentBodyEditor?.editor
    if (editor == null) return null

    const suggestion = selectedSuggestion ?? currentHighlightedSuggestion.value
    if (suggestion == null) return null

    mentioner = mentioner ?? currentUser.value
    if (mentioner == null) return null

    const mentionData = {
      type: suggestion.type,
      mentionerId: mentioner.hashid,
      mentioneeId: suggestion.attributes.hashid,
      mentioneeUsername: suggestion.attributes.username,
      mentioneeDisplayName: suggestion.attributes.display_name,
    }

    const range =
      mentionRange != null ? mentionRange : [editor.view.state.selection.from - 1, editor.view.state.selection.from]

    editor
      .chain()
      .deleteRange({
        from: range[0],
        to: range[1],
      })
      .run()

    editor
      .chain()
      .insertContentAt(range[0], {
        type: 'pdlt-mention', // This matches the node name defined in the Mention Node
        attrs: mentionData,
      })
      .run()

    hideMentionBox()

    return suggestion
  }

  /*
   * LOADING MENTION TAGS
   *
   * Trix cannot load the <pdlt-mention> tag directly. It will strip it out as part of its sanitization process.
   * In order to load and display the <pdlt-mention> tag correctly in the post editor, we need a strategy:
   * 1. Preprocess the HTML and replace all <pdlt-mention> tags with a substitute string.
   *    This string should have never appeared and is very unlikely to appear in any post body.
   * 2. Load the preprocessed HTML into the editor. The substitute string can make sure we don't lose track of
   *    where the <pdlt-mention> tags are.
   * 3. Get a string representation of the editor's content and replace the substitute string with the original
   *    <pdlt-mention> tags (insert as Trix attachment).
   */

  // At the time of writing, this sequence of characters has never appeared in any post body. To make it even
  // harder for someone to "accidentally" type out this exact sequence, and for us to distinguish each mention
  // later, we use some Unicode characters and put the mentioner ID - mentionee ID pair in the middle to create
  // the substitute string.
  const SUBSTITUTE_OPENING = '❮pdlt-mention❦'
  const SUBSTITUTE_CLOSING = '❩'
  const VALID_SUBSTITUTE_STRING_REGEX = new RegExp(
    `^${SUBSTITUTE_OPENING}[a-zA-Z0-9-_]+:[a-zA-Z0-9-_]+${SUBSTITUTE_CLOSING}$`,
  )

  const isValidSubstituteString = (substitute: string): boolean => {
    return VALID_SUBSTITUTE_STRING_REGEX.test(substitute)
  }

  const createSubstituteString = (mentionerId: string, mentioneeId: string): string => {
    return `${SUBSTITUTE_OPENING}${mentionerId}:${mentioneeId}${SUBSTITUTE_CLOSING}`
  }

  /**
   * Replaces all <pdlt-mention> tags with substitute strings. The substitute string for each mention tag
   * should also be unique so that we can retrieve the correct <pdlt-mention> tag later.
   */
  const preprocessHtml = (html: string): { processedHtml: string; mentionSubstituteMap: Map<string, PdltMention> } => {
    const pdltMentionTagRegex = /<pdlt-mention(.*?)>(.*?)<\/pdlt-mention>/gs
    const mentionSubstituteMap = new Map<string, PdltMention>()
    const processedHtml = html.replace(pdltMentionTagRegex, (match) => {
      const mention = PdltMention.fromString(match)
      if (mention?.isValid === true && mention.mentionerId !== null && mention.mentioneeId !== null) {
        const substitute = createSubstituteString(mention.mentionerId, mention.mentioneeId)
        mentionSubstituteMap.set(substitute, mention)
        return substitute
      }
      return match
    })
    return { processedHtml, mentionSubstituteMap }
  }

  /**
   * Finds all the substitute strings, deletes them, and inserts the original <pdlt-mention> tags
   * at the same position.
   */
  const postprocessHtml = (html: string, mentionSubstituteMap: Map<string, PdltMention>): void => {
    if (mentionSubstituteMap.size === 0) return

    // Use Trix to get a string representation of the given HTML. Shouldn't use any different
    // methods otherwise the ranges of the substitute strings may be incorrect.
    const htmlString: string = Trix.Document.fromHTML(html).toString()
    if (htmlString === html) return

    const ranges: Array<{
      start: number // `start` and `end` are the indices of the substitute string
      end: number // They're used to select and delete the substitute string later.
      tag: string // The original <pdlt-mention> tag to be inserted.
    }> = []
    let startIndex = -1
    let lastIndex = 0
    while ((startIndex = htmlString.indexOf(SUBSTITUTE_OPENING, lastIndex)) !== -1) {
      const start = startIndex
      const end = htmlString.indexOf(SUBSTITUTE_CLOSING, start) + SUBSTITUTE_CLOSING.length
      const substitute = htmlString.substring(start, end)
      if (isValidSubstituteString(substitute) && mentionSubstituteMap.has(substitute)) {
        const mention = mentionSubstituteMap.get(substitute) as PdltMention
        const tag = mention.toString()
        ranges.push({ start, end, tag })
      }
      lastIndex = end
    }

    // We replace the substitute strings from the end to the beginning so it doesn't affect the
    // earlier ranges.
    for (let i = ranges.length - 1; i >= 0; i--) {
      const { start, end, tag } = ranges[i]
      postBodyEditor?.editor.setSelectedRange([start, end])
      postBodyEditor?.editor.deleteInDirection('backward')
      const mention = new Trix.Attachment({ content: tag, contentType: 'mention' })
      postBodyEditor?.editor.insertAttachment(mention)
    }
  }

  const loadHtmlToPostBody = (html: string): void => {
    // When running this function, we want our text editor to not emit any updates
    // This is so that we don't unintentionally modify our global state when we make intermediary editor.set/delete/insert statements.
    if (editorLock != null) editorLock.value = true
    const { processedHtml, mentionSubstituteMap } = preprocessHtml(html)
    postBodyEditor?.editor.loadHTML(processedHtml)
    postprocessHtml(processedHtml, mentionSubstituteMap)
    if (editorLock != null) editorLock.value = false
  }

  return {
    insertMentionToPostBody,
    insertMentionToCommentBody,
    loadHtmlToPostBody,
  }
}
